import { useCallback, useMemo, useState } from 'react';

import {
  useDeleteCertificateMutation,
  useGetCertificatesQuery,
  type ModelCertificate,
} from '@app/api/Certificates';
import { FRONT_WARNING, SUCCESS, WARNING } from '@app/constants/alerts';
import {
  useNewUserAccessRights,
  useUserPrint,
  useWindowSize,
} from '@app/hooks';
import { useHeader } from '@app/providers';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { handleCatchError } from '@app/services';

import {
  CERTIFICATES_DEFAULT_PAGE_SIZE,
  CERTIFICATES_EMPTY_NAME,
} from '../data/constants';
import { getCertificatesColumns } from '../data/getCertificatesColumns';
import { clampPage } from '../utils/clampPage';

import type { TablePaginationConfig } from 'antd';
import type { ChangeEventHandler } from 'react';

// Отдельного права на сертификаты нет, поэтому реестр опирается на модуль
// «Клиенты», как и реестр абонементов.
export const useCertificates = () => {
  const {
    params: { ActiveBranchId },
  } = useUserPrint();
  const {
    userAccessRights: { Clients },
  } = useNewUserAccessRights();
  const { setHeader } = useHeader();
  const { showNotification } = useNotification();
  const { isMobile } = useWindowSize();

  const [unsavedSearchTerm, setUnsavedSearchTerm] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  // GetCertificates отдаёт весь список филиала, поэтому пагинация клиентская
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(CERTIFICATES_DEFAULT_PAGE_SIZE);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCertificateId, setEditingCertificateId] = useState<
    number | null
  >(null);
  // Сертификат не сбрасывается при закрытии окна подтверждения, чтобы текст
  // не менялся во время анимации закрытия
  const [deletingCertificate, setDeletingCertificate] =
    useState<ModelCertificate | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // Поиск по названию выполняет бэкенд
  const { data: certificates = [], isFetching } = useGetCertificatesQuery(
    { ActiveBranchId, searchTerm: searchTerm || undefined },
    { skip: !ActiveBranchId }
  );

  const [deleteCertificate, { isLoading: isDeleting }] =
    useDeleteCertificateMutation();

  const canView = Boolean(Clients?.IsOn);
  const canEdit = Boolean(Clients?.IsEdit);
  const canDelete = Boolean(Clients?.IsDelete);

  const onChangeSearchTerm: ChangeEventHandler<HTMLInputElement> = useCallback(
    ({ target: { value } }) => setUnsavedSearchTerm(value),
    []
  );

  const onSearch = useCallback(() => {
    setSearchTerm(unsavedSearchTerm.trim());
    setPage(1);
  }, [unsavedSearchTerm]);

  const onReset = useCallback(() => {
    setUnsavedSearchTerm('');
    setSearchTerm('');
    setPage(1);
  }, []);

  const openCreateModal = useCallback(() => {
    setEditingCertificateId(null);
    setIsModalOpen(true);
  }, []);

  const onEdit = useCallback(({ Id }: ModelCertificate) => {
    setEditingCertificateId(Id);
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingCertificateId(null);
  }, []);

  const onDelete = useCallback((certificate: ModelCertificate) => {
    setDeletingCertificate(certificate);
    setIsDeleteModalOpen(true);
  }, []);

  const deleteModalContent = useMemo(() => {
    const name = deletingCertificate?.Name?.trim() || CERTIFICATES_EMPTY_NAME;

    return `Вы уверены, что хотите удалить сертификат «${name}»?`;
  }, [deletingCertificate]);

  const confirmDelete = useCallback(async () => {
    if (!deletingCertificate) return;

    if (!ActiveBranchId) {
      showNotification('Филиал не был распознан', FRONT_WARNING);
      return;
    }

    try {
      await deleteCertificate({
        ActiveBranchId,
        certificateId: deletingCertificate.Id,
      }).unwrap();

      showNotification('Сертификат удалён', SUCCESS);
      setIsDeleteModalOpen(false);
    } catch (error) {
      showNotification(handleCatchError(error), WARNING);
    }
  }, [
    ActiveBranchId,
    deleteCertificate,
    deletingCertificate,
    showNotification,
  ]);

  const onChangePage = useCallback((nextPage: number, nextPageSize: number) => {
    setPage(nextPage);
    setPageSize(nextPageSize);
  }, []);

  // Страница в пределах текущего списка: эффект записывает её в состояние,
  // а таблица показывает её уже на этом рендере
  const clampedPage = clampPage({
    page,
    total: certificates.length,
    pageSize,
  });

  const pagination = useMemo<TablePaginationConfig | false>(
    () =>
      certificates.length > 0
        ? {
            current: clampedPage,
            pageSize,
            total: certificates.length,
            showSizeChanger: true,
            showLessItems: isMobile,
            size: isMobile ? 'small' : 'default',
            position: isMobile ? ['bottomCenter'] : ['bottomLeft'],
            onChange: onChangePage,
            showTotal: (total, [from, to]) =>
              isMobile
                ? `Всего ${total}`
                : `Показаны результаты с ${from} по ${to} из ${total}`,
          }
        : false,
    [certificates.length, clampedPage, isMobile, onChangePage, pageSize]
  );

  const columns = useMemo(
    () => getCertificatesColumns({ onEdit, onDelete, canEdit, canDelete }),
    [onEdit, onDelete, canEdit, canDelete]
  );

  return {
    certificates,
    columns,
    isLoading: isFetching,
    pagination,
    page,
    clampedPage,
    setPage,
    isSearchApplied: Boolean(searchTerm),
    isMobile,
    canView,
    unsavedSearchTerm,
    onChangeSearchTerm,
    onSearch,
    onReset,
    isModalOpen,
    editingCertificateId,
    openCreateModal,
    closeModal,
    setHeader,
    isDeleteModalOpen,
    deleteModalContent,
    isDeleting,
    setIsDeleteModalOpen,
    confirmDelete,
  };
};
